
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { 
  SiteConfig, 
  SitePage, 
  PageSection, 
  SiteMedia, 
  SiteTheme, 
  CMSComponent, 
  SitePopup, 
  SiteAnalytics, 
  CustomForm, 
  FormSubmission, 
  SiteMenu 
} from '@/types/cms'

// Hook pour la configuration du site
export const useSiteConfig = () => {
  return useQuery({
    queryKey: ['site-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_config')
        .select('*')
        .order('section, key')

      if (error) throw error
      return data as SiteConfig[]
    },
  })
}

export const useUpdateSiteConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ section, key, value, type }: { section: string, key: string, value: string, type: string }) => {
      const { data, error } = await supabase
        .from('site_config')
        .upsert({ section, key, value, type, updated_at: new Date().toISOString() })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-config'] })
    },
  })
}

// Hook pour les pages
export const useSitePages = () => {
  return useQuery({
    queryKey: ['site-pages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_pages')
        .select('*')
        .order('slug')

      if (error) throw error
      return data as SitePage[]
    },
  })
}

export const useCreateSitePage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (page: Omit<SitePage, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('site_pages')
        .insert(page)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-pages'] })
    },
  })
}

export const useUpdateSitePage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SitePage> & { id: string }) => {
      const { data, error } = await supabase
        .from('site_pages')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-pages'] })
    },
  })
}

// Hook pour les sections de pages
export const usePageSections = (pageId?: string) => {
  return useQuery({
    queryKey: ['page-sections', pageId],
    queryFn: async () => {
      let query = supabase
        .from('page_sections')
        .select('*')
        .order('section_order')

      if (pageId) {
        query = query.eq('page_id', pageId)
      }

      const { data, error } = await query
      if (error) throw error
      return data as PageSection[]
    },
    enabled: !!pageId,
  })
}

export const useCreatePageSection = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (section: Omit<PageSection, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('page_sections')
        .insert(section)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-sections'] })
    },
  })
}

export const useUpdatePageSection = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PageSection> & { id: string }) => {
      const { data, error } = await supabase
        .from('page_sections')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-sections'] })
    },
  })
}

// Hook pour les médias
export const useSiteMedia = () => {
  return useQuery({
    queryKey: ['site-media'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_media')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as SiteMedia[]
    },
  })
}

export const useUploadMedia = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ file, usageType, altText }: { file: File, usageType?: string, altText?: string }) => {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}.${fileExt}`
      const filePath = `${fileName}`

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('cms-media')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      // Get public URL
      const { data } = supabase.storage
        .from('cms-media')
        .getPublicUrl(filePath)

      // Save media info to database
      const { data: mediaData, error: dbError } = await supabase
        .from('site_media')
        .insert({
          name: fileName,
          original_name: file.name,
          file_path: data.publicUrl,
          file_size: file.size,
          mime_type: file.type,
          alt_text: altText,
          usage_type: usageType,
        })
        .select()
        .single()

      if (dbError) throw dbError
      return mediaData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-media'] })
    },
  })
}

// Hook pour les thèmes
export const useSiteThemes = () => {
  return useQuery({
    queryKey: ['site-themes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_themes')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as SiteTheme[]
    },
  })
}

// Hook pour les composants CMS
export const useCMSComponents = () => {
  return useQuery({
    queryKey: ['cms-components'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cms_components')
        .select('*')
        .order('category, name')

      if (error) throw error
      return data as CMSComponent[]
    },
  })
}

// Hook pour les pop-ups
export const useSitePopups = () => {
  return useQuery({
    queryKey: ['site-popups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_popups')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as SitePopup[]
    },
  })
}

export const useCreateSitePopup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (popup: Omit<SitePopup, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('site_popups')
        .insert(popup)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-popups'] })
    },
  })
}

// Hook pour l'analytics
export const useSiteAnalytics = (timeRange?: string) => {
  return useQuery({
    queryKey: ['site-analytics', timeRange],
    queryFn: async () => {
      let query = supabase
        .from('site_analytics')
        .select('*')
        .order('visited_at', { ascending: false })

      if (timeRange) {
        const date = new Date()
        if (timeRange === '7d') {
          date.setDate(date.getDate() - 7)
        } else if (timeRange === '30d') {
          date.setDate(date.getDate() - 30)
        }
        query = query.gte('visited_at', date.toISOString())
      }

      const { data, error } = await query
      if (error) throw error
      return data as SiteAnalytics[]
    },
  })
}

export const useTrackAnalytics = () => {
  return useMutation({
    mutationFn: async (analytics: Omit<SiteAnalytics, 'id' | 'visited_at'>) => {
      const { data, error } = await supabase
        .from('site_analytics')
        .insert(analytics)
        .select()
        .single()

      if (error) throw error
      return data
    },
  })
}

// Hook pour les formulaires personnalisés
export const useCustomForms = () => {
  return useQuery({
    queryKey: ['custom-forms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('custom_forms')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as CustomForm[]
    },
  })
}

// Hook pour les menus
export const useSiteMenus = () => {
  return useQuery({
    queryKey: ['site-menus'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_menus')
        .select('*')
        .order('location, name')

      if (error) throw error
      return data as SiteMenu[]
    },
  })
}
